import { useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import Icon from '@components/Icon';
import { Button, Tooltip, Modal, Radio, Space, Divider } from 'antd';
import stores from '@stores';
import Cropper from "react-cropper";
import { getMargin } from "@utils/utils";
import "cropperjs/dist/cropper.css";

// 预设裁剪比例配置（标签将在组件内通过翻译获取）
const cropRatiosConfig = [
    { labelKey: 'crop.free', value: 'free', ratio: NaN },
    { labelKey: 'crop.ratio16_9', value: '16:9', ratio: 16/9 },
    { labelKey: 'crop.ratio9_16', value: '9:16', ratio: 9/16 },
    { labelKey: 'crop.ratio4_3', value: '4:3', ratio: 4/3 },
    { labelKey: 'crop.ratio3_4', value: '3:4', ratio: 3/4 },
    { labelKey: 'crop.ratio1_1', value: '1:1', ratio: 1 },
    { labelKey: 'crop.ratio3_2', value: '3:2', ratio: 3/2 },
    { labelKey: 'crop.ratio2_3', value: '2:3', ratio: 2/3 },
    { labelKey: 'crop.ratio2_1', value: '2:1', ratio: 2/1 },
    { labelKey: 'crop.ratio1_2', value: '1:2', ratio: 1/2 },
    { labelKey: 'crop.ratio5_4', value: '5:4', ratio: 5/4 },
    { labelKey: 'crop.ratio4_5', value: '4:5', ratio: 4/5 },
    { labelKey: 'crop.ratio8_5', value: '8:5', ratio: 8/5 },
    { labelKey: 'crop.ratio5_8', value: '5:8', ratio: 5/8 },
    { labelKey: 'crop.ratio21_9', value: '21:9', ratio: 21/9 },
    { labelKey: 'crop.ratio6_13', value: '6:13', ratio: 6/13 },
];

export default observer(() => {
    const { t } = useTranslation('toolbar');
    const cropperRef = useRef(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRatio, setSelectedRatio] = useState('free');
    
    const handleCrop = () => {
        setIsModalOpen(true);
    };
    
    const handleOk = () => {
        if (typeof cropperRef.current?.cropper !== "undefined") {
            const canvas = cropperRef.current?.cropper.getCroppedCanvas();
            if (canvas) {
                const { width, height } = canvas;
                const imgUrl = canvas.toDataURL();
                stores.editor.setImg(Object.assign({}, stores.editor.img, {
                    src: imgUrl,
                    width,
                    height,
                }));
                if (stores.option.size.type === 'auto') {
                    const margin = getMargin(width, height);
                    stores.option.setFrameSize(width + margin, height + margin);
                }
            }
        }
        setIsModalOpen(false);
    };
    
    const handleCancel = () => {
        setIsModalOpen(false);
        // 重置为自由裁剪
        setSelectedRatio('free');
    };
    
    const handleRatioChange = (e) => {
        const value = e.target.value;
        setSelectedRatio(value);
        
        const selectedOption = cropRatiosConfig.find(ratio => ratio.value === value);
        if (selectedOption && cropperRef.current?.cropper) {
            // 对于自由裁剪，设置为undefined以释放比例限制
            cropperRef.current.cropper.setAspectRatio(selectedOption.ratio);
        }
    };
    
    return (
        <>
            <Tooltip title={t('crop.tooltip')}>
                <Button
                    type='text'
                    shape='circle'
                    className="crop-button-trigger"
                    icon={<Icon.Crop size={18} />}
                    onClick={handleCrop}
                ></Button>
            </Tooltip>
            <Modal
                title={
                    <div className="flex items-center text-purple-600 dark:text-purple-400">
                        <Icon.Crop size={20} className="mr-2" />
                        <span>{t('crop.title')}</span>
                    </div>
                }
                open={isModalOpen}
                onOk={handleOk}
                onCancel={handleCancel}
                destroyOnClose={true}
                okText={t('crop.okText')}
                cancelText={t('crop.cancelText')}
                width={720}
                className="crop-modal"
                okButtonProps={{ 
                    className: "bg-purple-600 hover:bg-purple-700 border-purple-600 hover:border-purple-700" 
                }}
                cancelButtonProps={{
                    className: "hover:text-purple-600 hover:border-purple-600"
                }}
                maskStyle={{ backdropFilter: "blur(4px)" }}
                bodyStyle={{ padding: "12px", background: stores.editor.isDark ? "#1f2937" : "#f8fafc" }}
                style={{ top: 20 }}
            >
                <div className="mb-3 bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm">
                    <Divider orientation="left" orientationMargin="0" className="text-xs text-gray-500 dark:text-gray-400 my-1">
                        <div className="flex items-center">
                            <Icon.Maximize size={12} className="mr-1 text-purple-500 dark:text-purple-400" />
                            {t('crop.ratio')}
                        </div>
                    </Divider>
                    <Radio.Group 
                        value={selectedRatio} 
                        onChange={handleRatioChange}
                        className="w-full flex flex-wrap"
                        buttonStyle="solid"
                    >
                        <Space wrap size={[6, 8]} className="w-full justify-center">
                            {cropRatiosConfig.map(ratio => (
                                <Radio.Button 
                                    key={ratio.value} 
                                    value={ratio.value}
                                    className={`text-xs px-2 py-0.5 rounded-full transition-all ${
                                        selectedRatio === ratio.value 
                                            ? 'bg-purple-600 text-white border-purple-600 dark:bg-purple-500 dark:border-purple-500' 
                                            : 'border-gray-300 dark:border-gray-600 dark:text-gray-300 hover:text-purple-600 hover:border-purple-600 dark:hover:text-purple-400 dark:hover:border-purple-400'
                                    }`}
                                >
                                    {t(ratio.labelKey)}
                                </Radio.Button>
                            ))}
                        </Space>
                    </Radio.Group>
                </div>
                
                <div className="bg-white dark:bg-gray-800 p-2 rounded-lg shadow-sm overflow-hidden">
                    <Cropper
                        ref={cropperRef}
                        style={{ height: 360, width: "100%" }}
                        zoomTo={0.5}
                        initialAspectRatio={stores.editor.img.width / stores.editor.img.height}
                        aspectRatio={cropRatiosConfig.find(ratio => ratio.value === selectedRatio)?.ratio}
                        src={stores.editor.img.src}
                        dragMode="move"
                        viewMode={1}
                        minCropBoxHeight={10}
                        minCropBoxWidth={10}
                        background={false}
                        responsive={true}
                        autoCropArea={1}
                        checkOrientation={false}
                        guides={true}
                        modal={true}
                        highlight={false}
                        cropBoxMovable={true}
                        cropBoxResizable={true}
                        className="rounded-md"
                    />
                </div>
                
                <div className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
                    <p>{t('crop.hint')}</p>
                </div>
            </Modal>
        </>
    );
});
